import {
  AccountType,
  getV1CompaniesCompanyIdDrivers,
  GetV1CompaniesCompanyIdDriversData,
  getV1CompaniesCompanyIdDriversDriverIdDeviceStatus,
  getV1CompaniesCompanyIdDriversId,
  getV1CompaniesCompanyIdDriversTypeahead,
  GetV1CompaniesCompanyIdDriversTypeaheadData,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDrivers,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDriversId,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDriversTypeahead,
  postV1CompaniesCompanyIdAccountsInvite,
  putV1CompaniesCompanyIdDriversDriverIdDriverStateOffDuty,
  putV1CompaniesCompanyIdUsersUserIdScheduledTimeOffsAvailable,
  putV1CompaniesCompanyIdUsersUserIdScheduledTimeOffsUnavailable,
  User_Create,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useState } from 'react';

import { DriverFormValues } from '~components/DriverForm/DriverForm';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Account } from '~hooks/useAccount/models';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import useCancelToken from '~hooks/useCancelToken/useCancelToken';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { DeviceStatusRead } from '~hooks/useDeviceStatus/models';
import { Driver, DriverTypeahead } from '~hooks/useDrivers/models';
import { Equipment } from '~hooks/useEquipment/models';
import { Filters } from '~pages/Settings/Administration/Drivers/hooks/useDriverFilters';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface GetDriversByCompanyIdProps {
  companyId: string;
  managedCompanyId?: string;
  link?: PaginationLink;
  cursor?: string;
  limit?: number;
  dispatchable?: boolean;
  shared?: boolean;
  filters?: Filters;
  searchQuery?: string;
  signal?: AbortSignal;
}

interface GetDriversTypeaheadParams {
  companyId: string;
  managedCompanyId?: string;
  shared?: boolean;
  dispatchable?: boolean;
  availability?: boolean;
  date?: string;
  searchParams?: {
    limit?: number;
    query?: string;
    link?: {
      type: PaginationLink;
      cursor: string;
    };
  };
}

export const useDrivers = () => {
  const { userStore } = useStores();
  const selectedCompanyId = getEffectiveUserCompanyId(userStore);
  const { getCompanyById } = useCompany();
  const [isLoading, setIsLoading] = useState(false);

  const createDriverByCompanyId = async (
    driver: DriverFormValues,
    inviteTypes: User_Create['send_invite'] = {},
    accountId?: Nullable<string>,
    defaultAccountAddress?: Nullable<AddressItem>,
  ) => {
    setIsLoading(true);

    try {
      const targetCompanyId = accountId || (driver.company as CompanyBasic).id;
      let driverAddress = driver.address;

      if (!driverAddress) {
        if (accountId && defaultAccountAddress) {
          driverAddress = defaultAccountAddress;
        } else {
          const company = await getCompanyById({ id: targetCompanyId });
          driverAddress = company?.address;
        }
      }
      const fullName = `${driver.firstName} ${driver.lastName}`;
      const invitesToSend = {
        email: inviteTypes?.email || false,
        sms: inviteTypes?.sms || false,
      };

      let internalEquipment: ReturnType<typeof Equipment.deparseInternal> | undefined;
      if (driver.createInternalEquipment) {
        if (!driver.equipmentType?.id) {
          throw new Error('Equipment type is required when creating internal equipment');
        }

        internalEquipment = Equipment.deparseInternal({
          name: driver.equipment?.name || '',
          type: { id: driver.equipmentType.id },
        });
      }

      const updatedAccount = {
        ...driver,
        name: driver.accountName || fullName,
        externalId: driver.accountExternalId || undefined,
        address: driverAddress,
        primaryContact: {
          name: fullName,
          email: driver.email ?? undefined,
          phone: driver.phone ?? undefined,
        },
        invitesToSend,
        accountTypes: [AccountType.VENDOR],
        companyType: 'owner_operator',
        autoAcceptOrders: false,
        scopedCompanyId: selectedCompanyId,
        ...(!driver.createInternalEquipment && {
          equipment: driver.equipment?.name
            ? {
                name: driver.equipment?.name,
                equipment_type_name: driver.equipmentType?.name,
              }
            : undefined,
          internalEquipment: undefined,
        }),
        ...(driver.createInternalEquipment && {
          equipment: undefined,
          internalEquipment,
        }),
      };

      const response = await postV1CompaniesCompanyIdAccountsInvite({
        path: { 'company-id': targetCompanyId },
        body: Account.deparseConnectedAccount(updatedAccount),
      });

      return Account.parse(response.data.data);
    } finally {
      setIsLoading(false);
    }
  };

  const getDriverById = async (
    id: string,
    companyId?: string,
    managedCompanyId?: string,
  ) => {
    try {
      setIsLoading(true);
      const driverCompanyId = companyId || selectedCompanyId;

      const response = managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDriversId({
            path: {
              'company-id': driverCompanyId,
              'managed-company-id': managedCompanyId,
              id,
            },
          })
        : await getV1CompaniesCompanyIdDriversId({
            path: { 'company-id': driverCompanyId, id },
          });

      return Driver.parse(response.data.data);
    } catch {
      throw new Error(t('error_messages.drivers.failed_to_fetch_driver'));
    } finally {
      setIsLoading(false);
    }
  };

  const getDriversByCompanyId = async ({
    companyId,
    managedCompanyId,
    link,
    cursor,
    shared,
    limit,
    dispatchable,
    searchQuery,
    filters,
    signal,
  }: GetDriversByCompanyIdProps) => {
    const query: GetV1CompaniesCompanyIdDriversData['query'] = {
      'page[limit]': limit || MAX_API_PAGE_SIZE,
    };
    if (searchQuery) {
      query['search[datagrid]'] = searchQuery;
    }
    if (link && cursor) {
      query[`page[${link}]`] = cursor;
    }
    if (!isNil(shared)) {
      query['filter[shared]'] = shared;
    }
    if (!isNil(dispatchable)) {
      query['filter[dispatchable]'] = dispatchable;
    }
    if (!isNil(filters?.senderCompanyTypes)) {
      query['filter[sender_company_types]'] = filters.senderCompanyTypes;
    }
    if (!isNil(filters?.labels)) {
      query['filter[label_ids]'] = filters.labels;
    }

    try {
      setIsLoading(true);
      const response = managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDrivers({
            path: { 'company-id': companyId, 'managed-company-id': managedCompanyId },
            query,
            signal,
          })
        : await getV1CompaniesCompanyIdDrivers({
            path: { 'company-id': companyId },
            query,
            signal,
          });

      return {
        data: response.data.data.map(Driver.parse),
        pagination: extractPagination(response),
      };
    } catch (e) {
      if (signal?.aborted) {
        throw e;
      }

      connection.handleRequestError(
        e,
        t('error_messages.drivers.failed_to_fetch_drivers'),
      );
      // the toast above is the user-facing report; rethrowing only stops the caller
      throw e;
    } finally {
      // an aborted call was superseded by a request that still owns the loading flag
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const getDriversByCompanyIdTypeaheadCancelToken = useCancelToken();
  const getDriversByCompanyIdTypeahead = async (options: GetDriversTypeaheadParams) => {
    const query: GetV1CompaniesCompanyIdDriversTypeaheadData['query'] = {
      'filter[availability]': options.availability,
      'filter[dispatchable]': isNil(options.dispatchable)
        ? undefined
        : options.dispatchable,
      'filter[shared]': isNil(options.shared) ? undefined : options.shared,
      'filter[date]': options.date,
      'page[after]':
        options?.searchParams?.link?.cursor &&
        options?.searchParams?.link.type === 'after'
          ? options.searchParams.link.cursor
          : undefined,
      'page[before]':
        options?.searchParams?.link?.cursor &&
        options?.searchParams?.link.type === 'before'
          ? options.searchParams.link.cursor
          : undefined,
      'page[limit]': options.searchParams?.limit || MAX_API_PAGE_SIZE,
      'search[query]': options.searchParams?.query?.trim() || undefined,
    };

    try {
      setIsLoading(true);

      const cancelToken = getDriversByCompanyIdTypeaheadCancelToken.issueToken();
      const response = options.managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDriversTypeahead({
            path: {
              'company-id': options.companyId,
              'managed-company-id': options.managedCompanyId,
            },
            query,
            cancelToken,
          })
        : await getV1CompaniesCompanyIdDriversTypeahead({
            path: { 'company-id': options.companyId },
            query,
            cancelToken,
          });

      const drivers = response.data.data.map((driver) => DriverTypeahead.parse(driver));
      const pagination = extractPagination(response);

      return { data: drivers, pagination };
    } catch (e) {
      if (axios.isCancel(e)) {
        return {
          data: [],
          pagination: {
            page: 1,
            limit: options.searchParams?.limit || MAX_API_PAGE_SIZE,
          },
        };
      }

      connection.handleRequestError(
        e,
        t('error_messages.drivers.failed_to_fetch_drivers'),
      );
    } finally {
      getDriversByCompanyIdTypeaheadCancelToken.discardToken();
      setIsLoading(false);
    }
  };

  const getDriverByID = async (driverId: string) => {
    setIsLoading(true);
    try {
      const response = await getV1CompaniesCompanyIdDriversId({
        path: { 'company-id': selectedCompanyId, id: driverId },
      });
      const formatted = Driver.parse(response.data.data);
      return formatted;
    } finally {
      setIsLoading(false);
    }
  };

  const markDriverAsAvailable = async (driverId: string) => {
    setIsLoading(true);
    try {
      await putV1CompaniesCompanyIdUsersUserIdScheduledTimeOffsAvailable({
        path: { 'company-id': selectedCompanyId, 'user-id': driverId },
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.drivers.failed_to_mark_driver_as_available'),
        [422],
      );
    } finally {
      setIsLoading(false);
    }
  };

  const markDriverAsUnavailable = async (driverId: string) => {
    setIsLoading(true);
    try {
      await putV1CompaniesCompanyIdUsersUserIdScheduledTimeOffsUnavailable({
        path: { 'company-id': selectedCompanyId, 'user-id': driverId },
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.drivers.failed_to_mark_driver_as_unavailable'),
        [422],
      );
    } finally {
      setIsLoading(false);
    }
  };

  const setDriverStateToOffDuty = async (driverId: string) => {
    setIsLoading(true);
    try {
      const response = await putV1CompaniesCompanyIdDriversDriverIdDriverStateOffDuty({
        path: {
          'company-id': selectedCompanyId,
          'driver-id': driverId,
        },
      });
      return response.data.data;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.drivers.failed_to_set_driver_state_to_off_duty'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getDriverDeviceStatus = async (driver: string, companyId: string) => {
    try {
      const response = await getV1CompaniesCompanyIdDriversDriverIdDeviceStatus({
        path: { 'company-id': companyId, 'driver-id': driver },
      });
      const deviceStatus = DeviceStatusRead.parse(response.data.data);
      return deviceStatus;
    } catch (error) {
      connection.handleRequestError(error);
      return null;
    }
  };

  return {
    createDriverByCompanyId,
    getDriverByID,
    getDriverById,
    getDriversByCompanyId,
    getDriversByCompanyIdTypeahead,
    getDriverDeviceStatus,
    markDriverAsAvailable,
    markDriverAsUnavailable,
    setDriverStateToOffDuty,
    isLoading,
  };
};
